import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  suportaAvisos,
  permissaoNegada,
  ligarAvisos,
  desligarAvisos,
  situacaoAvisos,
  estaLigadoPara,
} from '@/lib/avisos';

interface Props {
  eventoId: number;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

// Avisos de venda no celular deste organizador.
export default function BlocoAvisos({ eventoId }: Props) {
  const [estado, setEstado] = useState<'verificando' | 'normal' | 'ligado' | 'bloqueado'>(
    'verificando',
  );
  const [trocando, setTrocando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!suportaAvisos()) {
      setEstado('bloqueado');
      return;
    }
    if (permissaoNegada()) {
      setEstado('bloqueado');
      return;
    }
    let ativo = true;
    situacaoAvisos().then((sit) => {
      if (ativo) setEstado(estaLigadoPara(sit, eventoId) ? 'ligado' : 'normal');
    });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  async function alternar() {
    if (!suportaAvisos()) return;
    setTrocando(true);
    setErro(null);
    try {
      if (estado === 'ligado') {
        await desligarAvisos(eventoId);
        setEstado('normal');
        setToast('Avisos de venda desligados.');
      } else {
        await ligarAvisos({ evento: eventoId, carteira: null, papel: 'organizador' });
        setEstado('ligado');
        setToast('Pronto! Você será avisado a cada venda.');
      }
    } catch (e) {
      if (permissaoNegada()) setEstado('bloqueado');
      else setErro(mensagemAmigavel(e));
    } finally {
      setTrocando(false);
    }
  }

  return (
    <div className="space-y-3">
      {erro && (
        <div className="rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</div>
      )}

      <div className={CARD}>
        <div className="flex min-h-[56px] items-center justify-between gap-3">
          <div className="min-w-0">
            <p
              className={`text-[15px] font-semibold ${
                estado === 'bloqueado' ? 'text-foreground-500' : 'text-foreground-900'
              }`}
            >
              Avisar a cada venda neste celular
            </p>
            <p className="mt-0.5 text-[13px] text-foreground-500">
              {estado === 'bloqueado'
                ? 'Avisos bloqueados neste navegador.'
                : 'Recebe uma notificação quando alguém compra.'}
            </p>
          </div>
          {estado === 'bloqueado' ? (
            <p className="max-w-[180px] shrink-0 text-right text-[12px] leading-snug text-foreground-500">
              Para ligar: ⓘ ao lado do endereço › Notificações › Permitir.
            </p>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={estado === 'ligado'}
              aria-label="Avisar a cada venda neste celular"
              disabled={trocando || estado === 'verificando'}
              onClick={alternar}
              className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                estado === 'ligado' ? 'bg-primary-500' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${
                  estado === 'ligado' ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div className="rounded-2xl bg-foreground-950 px-4 py-3 text-[14px] text-white">
          {toast}
        </div>
      )}
    </div>
  );
}